<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="login-page-wrapper">
  <div class="login-container">
    <div class="login-box">
      <!-- Logo -->
      <div class="logo-section">
        <a href="https://bioimaging.vieworks.com/" target="_blank">
          <img src="@/assets/images/visque_dps_logo.png" id="logo" alt="Visque DPS">
        </a>
      </div>

      <!-- Forgot Username Form -->
      <template v-if="forgotUsername">
        <div class="form-header">
          <h2 class="form-title">{{$t('forgot-username')}}</h2>
        </div>
        <div class="form-content">
          <form @submit.prevent="sendUsername">
            <b-field :label="$t('email')" class="custom-field">
              <b-input v-model="email" class="custom-input" />
            </b-field>

            <div class="button-group">
              <button class="btn btn-secondary" type="button" @click="forgotUsername = false">
                {{$t('button-cancel')}}
              </button>
              <button class="btn btn-primary">
                {{$t('button-get-username')}}
              </button>
            </div>
          </form>
        </div>
      </template>

      <!-- Forgot Password Form -->
      <template v-else-if="forgotPassword">
        <div class="form-header">
          <h2 class="form-title">{{$t('forgot-password')}}</h2>
        </div>
        <div class="form-content">
          <form @submit.prevent="resetPassword()">
            <b-field :label="$t('username')" class="custom-field">
              <b-input v-model="username" class="custom-input" />
            </b-field>

            <div class="button-group">
              <button class="btn btn-secondary" type="button" @click="forgotPassword = false">
                {{$t('button-cancel')}}
              </button>
              <button class="btn btn-primary">
                {{$t('button-reset-password')}}
              </button>
            </div>
          </form>
        </div>
      </template>

      <!-- Register Form -->
      <register v-else-if="registering" @close="registering=false" />

      <!-- Login Form -->
      <template v-else>
        <div class="form-header">
          <h2 class="form-title">{{$t('login')}}</h2>
        </div>
        <div class="form-content">
          <form @submit.prevent="login()">
            <b-field :label="$t('username')" class="custom-field">
              <b-input minlength="1" maxlength="50" v-model="username" class="custom-input" />
            </b-field>

            <b-field :label="$t('password')" class="custom-field">
              <b-input
                :key="`pw-field-${showPassword}`"
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                class="custom-input"
                minlength="8"
                maxlength="100"
                :icon-right="showPassword ? 'eye' : 'eye-off'"
                icon-right-clickable
                @icon-right-click="showPassword = !showPassword"
              />
            </b-field>

            <div class="login-options">
              <b-field class="remember-me-field">
                <b-checkbox v-model="rememberMe" class="custom-checkbox">
                  {{$t('remember-me')}}
                </b-checkbox>
              </b-field>

              <button type="submit" :disabled="!loginEnabled" class="btn btn-primary btn-login">
                {{$t('button-login')}}
              </button>
            </div>
          </form>
        </div>
        
        <!-- <div class="footer-links">
          <i18n path="forgot-credentials" tag="p" class="forgot-credentials">
            <a place="username" @click="forgotUsername = true" class="link-text">{{ $t('username') }}</a>
            <a place="password" @click="forgotPassword = true" class="link-text">{{ $t('password') }}</a>
          </i18n>
          <i18n v-if="allowedRegistration" path="no-account-yet" tag="p" class="register-link">
            <a place="registerLink" @click="registering = true" class="link-text">{{$t('register')}}</a>
          </i18n>
        </div> -->
      </template>
    </div>
  </div>

  <!-- Bottom Right Info -->
  <div class="bottom-right-info">
    <p class="powered-by">{{ $t('powered-by-cytomine') }}</p>
    <p class="version-info">version {{ version }}</p>
  </div>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {changeLanguageMixin} from '@/lang.js';
import {Cytomine} from 'cytomine-client';
import Register from './Register';
import AboutCytomineModal from './AboutCytomineModal';

export default {
  // eslint-disable-next-line
  name: 'login',
  components: {Register},
  mixins: [changeLanguageMixin],
  data() {
    return {
      version: process.env.VUE_APP_VERSION,

      username: '',
      password: '',
      email: '',
      rememberMe: true,

      allowedRegistration: false, // TODO: retrieve info from core

      forgotUsername: false,
      forgotPassword: false,
      registering: false,
      showPassword: false,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    loginEnabled() {
      return !!this.username && !!this.password;
    }
  },
  methods: {
    async login() {
      try {
        await this.$store.dispatch('currentUser/login', {
          username: this.username,
          password: this.password,
          rememberMe: this.rememberMe
        });

        if (this.currentUser) {
          this.changeLanguage(this.currentUser.language);

          if (this.rememberMe) {
            localStorage.setItem('savedUsername', this.username);
            localStorage.setItem('rememberMe', 'true');
          }
          else {
            localStorage.removeItem('savedUsername');
            localStorage.setItem('rememberMe', 'false');
          }

          this.$notify({
            type: 'success',
            text: this.$t('notif-success-login')
          });
        }
        else {
          this.$notify({type: 'error', text: this.$t('notif-unexpected-error')});
        }
      }
      catch(error) {
        this.$notify({type: 'error', text: error.response.data.errors.message});
      }
    },
    async sendUsername() {
      try {
        await Cytomine.instance.forgotUsername(this.email);
        this.$notify({type: 'success', text: this.$t('notif-success-forgot-username')});
        this.forgotUsername = false;
      }
      catch(error) {
        this.$notify({type: 'error', text: this.$t('notif-error-forgot-username')});
      }
    },
    async resetPassword() {
      try {
        await Cytomine.instance.forgotPassword(this.username);
        this.$notify({type: 'success', text: this.$t('notif-success-forgot-password')});
        this.forgotPassword = false;
      }
      catch(error) {
        this.$notify({type: 'error', text: this.$t('notif-error-forgot-password')});
      }
    },
    openAboutModal() {
      this.$buefy.modal.open({
        parent: this,
        component: AboutCytomineModal,
        hasModalCard: true
      });
    }
  },
  created() {
    const savedUsername = localStorage.getItem('savedUsername');
    const savedRememberMe = localStorage.getItem('rememberMe');

    if (savedRememberMe !== null) {
      this.rememberMe = savedRememberMe === 'true';
    }

    if (savedUsername) {
      this.username = savedUsername;
    }
  }
};
</script>

<style scoped lang="scss">
.login-page-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
  padding: 20px;
}

.login-container {
  position: relative;
  width: 100%;
  max-width: 480px;
}

.login-box {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.logo-section {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 20px 20px;
  background-color: #F4F4F4;
  
  a {
    display: block;
  }
}

#logo {
  width: 200px;
  height: auto;
  max-width: 100%;
}

.form-header {
  padding: 30px 40px 20px;
  text-align: center;
  background-color: #ffffff;
}

.form-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: #4A4847;
  margin: 0;
}

.form-content {
  padding: 0 40px 30px;
  
  form {
    width: 100%;
  }
}

.custom-field {
  margin-bottom: 1.5rem;
  
  ::v-deep .label {
    color: #4A4847;
    font-weight: 500;
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
}

.custom-input {
  ::v-deep .input {
    border-radius: 8px;
    border: 1.5px solid #e0e0e0;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: #009BDB;
      box-shadow: 0 0 0 3px rgba(0, 155, 219, 0.1);
    }
  }
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  gap: 1rem;
}

.remember-me-field {
  margin-bottom: 0;
  
  ::v-deep .b-checkbox.checkbox {
    .control-label {
      color: #4A4847;
      font-size: 0.9rem;
    }
    
    input[type="checkbox"]:checked + .check {
      background-color: #009BDB;
      border-color: #009BDB;
    }
  }
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-primary {
  background-color: #009BDB;
  color: #ffffff;
  
  &:hover:not(:disabled) {
    background-color: #0088c2;
    box-shadow: 0 4px 12px rgba(0, 155, 219, 0.3);
    transform: translateY(-1px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
}

.btn-secondary {
  background-color: #f4f4f4;
  color: #4A4847;
  
  &:hover {
    background-color: #e8e8e8;
  }
}

.btn-login {
  min-width: 120px;
}

.footer-links {
  padding: 20px 40px 30px;
  background-color: #fafafa;
  text-align: center;
  border-top: 1px solid #f0f0f0;
  
  p {
    margin: 0.5rem 0;
    font-size: 0.875rem;
    color: #666;
  }
}

.forgot-credentials {
  a {
    text-transform: lowercase;
  }
}

.link-text {
  color: #009BDB;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    color: #0088c2;
    text-decoration: underline;
  }
}

.register-link {
  margin-top: 1rem;
}

.bottom-right-info {
  position: fixed;
  bottom: 20px;
  right: 20px;
  text-align: right;
  z-index: 10;
}

.powered-by {
  color: #999;
  font-size: 0.75rem;
  margin: 0 0 4px 0;
}

.version-info {
  color: #999;
  font-size: 0.75rem;
  margin: 0;
}

/* Responsive Design */
@media screen and (max-width: 768px) {
  .login-page-wrapper {
    padding: 10px;
  }
  
  .form-content {
    padding: 0 30px 30px;
  }
  
  .form-header {
    padding: 25px 30px 15px;
  }
  
  .footer-links {
    padding: 15px 30px 25px;
  }
  
  .form-title {
    font-size: 1.5rem;
  }
  
  .login-options {
    flex-direction: column;
    align-items: stretch;
  }
  
  .btn-login {
    width: 100%;
  }
}

@media screen and (max-width: 480px) {
  #logo {
    width: 150px;
  }
  
  .form-content,
  .form-header,
  .footer-links {
    padding-left: 20px;
    padding-right: 20px;
  }
}
</style>
